<script setup lang="ts">
import RichResponseListItem from "@/views/Chat/RichResponses/components/RichResponseListItem.vue";
import {IonButton, IonIcon, IonLabel, IonToggle} from "@ionic/vue";
import {AbsenceType, BookingSuggestion, DemandJoDaShTiFragment} from "@/graphql/generated/graphql";
import {computed, PropType, Ref, ref} from "vue";
import datetimeRange from "@/helper/datetime/datetimeRange";
import {KapitelIcon} from "@/helper/icon";
import JobsiteDetails from "@/views/JobsiteDetails.vue";
import {getIconForAbsenceType} from "@/helper/absence";

const props = defineProps({
    joDaShTi: {
        type: Object as PropType<DemandJoDaShTiFragment>,
        required: true
    },
    showJobsiteLabel: {
        type: Boolean,
        required: false,
        default: false
    },
    amIAvailable: Boolean,
    toggleable: {
        type: Boolean,
        default: true,
        required: false,
    },
    toggleState: Boolean,
    instantBookable: {
        type: Boolean,
        default: false
    },
    suggestionLoading: {
        type: Boolean,
        default: false
    },
    suggestion: {
        type: Object as PropType<BookingSuggestion | undefined>,
        required: false
    }
})

const worktime = computed(() => datetimeRange.formatTimeRangeObject(props.joDaShTi))

const emit = defineEmits<{
    toggle: [state: boolean, d: DemandJoDaShTiFragment]
}>()

const state: Ref<boolean> = ref(props.toggleState)

const onToggle = () => {
    state.value = !state.value
    emit("toggle", state.value, props.joDaShTi);
};

const onInstantBook = () => {
    state.value = true
    emit("toggle", true, props.joDaShTi)
}

const autopilotConsiderationColor = computed(() => props.amIAvailable ? 'success' : 'dark')

const suggestionMessage = computed(() => props.suggestion?.request?.message)

const autopilotConsiderationLabel = computed(() => {
    if (props.amIAvailable && !props.toggleState) {
        return 'Verfügbar für diesen Dienst über die Einstellungen der Monatsplanung'
    } else if (props.amIAvailable && props.toggleState) {
        return 'Für diesen Dienst verfügbar'
    } else if (!props.amIAvailable && props.toggleState) {
        return 'Aktuell anderweitig besetzt, dennoch für diesen Dienst verfügbar'
    } else if (props.instantBookable) {
        if (props.suggestionLoading) return 'Sofort buchbar'
        const sug = props.suggestion
        if (!sug) return 'Sofort buchbar'
        const name = sug.withdrawerName ?? 'Kolleg:in'
        return `${name} möchte den Dienst abgeben.`
    }else {
        return 'Aktuell nicht verfügbar für diesen Dienst'
    }
})

const showJobsiteDetailsModalModel : Ref<number|undefined> = ref(undefined)

</script>

<template>
    <RichResponseListItem
        :icon="props.joDaShTi.shiftType"
        icon-type="text"
        :icon-color="autopilotConsiderationColor"
    >
        <template #title>
            <template v-if="props.showJobsiteLabel">
                {{ props.joDaShTi.jobsite.abbreviation }}
                <a @click="() => showJobsiteDetailsModalModel = props.joDaShTi.jobsite.id">
                    <IonIcon :icon="KapitelIcon.infoFill"  class="globalTextAlignedIcon"/>
                </a>
                <JobsiteDetails v-model="showJobsiteDetailsModalModel" :showJobsiteDemandModalLink="false" />
                <br/>
            </template>
            {{ worktime }}
        </template>

        <template #default>
            <div>
                <IonLabel :color="autopilotConsiderationColor">{{autopilotConsiderationLabel}}</IonLabel>
                <div v-if="props.instantBookable && suggestionMessage" class="suggestion-message">
                    {{ suggestionMessage }}
                </div>
                <IonButton
                    v-if="props.instantBookable"
                    color="secondary"
                    :disabled="props.suggestionLoading"
                    @click="onInstantBook"

                >Dienst übernehmen
                    <IonIcon :icon="KapitelIcon.caret" style="margin-left: 0.5em"/>
                </IonButton>
            </div>
        </template>

        <template #end>
            <IonToggle
                v-if="!props.instantBookable && props.toggleable"
                :color="autopilotConsiderationColor"
                :checked="props.toggleState"
                @ionChange="onToggle"
            />
        </template>
    </RichResponseListItem>
</template>

<style scoped lang="scss">
.suggestion-message {
    margin: 0.5em 0;
    padding-left: 0.75em;
    border-left: 3px solid var(--ion-color-medium);
    color: var(--ion-color-medium);
    font-style: italic;
    font-size: var(--custom-font-size-small);
    line-height: 1.3;
}
</style>
